import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    Configuration,
    TableRequest,
    UserData,
    User,
    UserLoginResponse,
    UserEditor,
    Permission,
    UserGroupEditor,
    CustomerGroupEditor,
    CustomerEditor,
    Category,
    CategoryEditor,
    ProductAttribute,
    ProductAttributeEditor,
    TableResponse,
    ProductPriceTypeEditor,
    ProductEditor,
    BannerEditor,
    OrderEditor,
    DownloadableFile,
    FaqEditor,
    PageEditor,
    AdminModuleDetail,
    AdminModuleLog,
    CurrencyEditor,
    AdminModuleGroup,
    Settings,
    ProductImageInfo,
    PaymentmethodEditor,
    ShippingMethodEditor,
    GratisItem,
    GratisEditor,
    BlogEditor
} from '../interfaces/interfaces';
import { ApiService as CFHCApiService } from 'codefoxhucore';

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    constructor(
        private apiService: CFHCApiService
    ) { }

    setApiBaseUrl(apiBaseUrl: string): void {
        this.apiService.apiBaseUrl = apiBaseUrl;
    }

    readConfig(): Observable<Configuration> {
        return this.apiService.get('#assets/config.json');
    }

    getApiBaseUrl(): string {
        return this.apiService.apiBaseUrl;
    }

    init(): Observable<{
        userData: UserData,
        sessionToken: string
    }> {
        return this.apiService.get('init');
    }

    login(request: {
        username: string,
        password: string,
        stayLoggedIn: boolean
    }): Observable<UserLoginResponse> {
        return this.apiService.post('user/login', request);
    }

    logout(): Observable<{}> {
        return this.apiService.delete('user/logout');
    }

    getUserGroup(id: number): Observable<{
        userGroupEditor: UserGroupEditor
    }> {
        return this.apiService.get('usergroups/' + id);
    }

    createUserGroup(request: UserGroupEditor): Observable<{}> {
        return this.apiService.post('usergroups', request);
    }

    updateUserGroup(id: number, request: UserGroupEditor): Observable<{}> {
        return this.apiService.patch('usergroups/' + id, request);
    }

    deleteUserGroup(id: number): Observable<{}> {
        return this.apiService.delete('usergroups/' + id);
    }

    getUsers(request: TableRequest): Observable<TableResponse<User>> {
        return this.apiService.get('users?request=' + JSON.stringify(request));
    }

    createUser(request: UserEditor): Observable<{}> {
        return this.apiService.post('users', request);
    }

    updateUser(id: number, request: UserEditor): Observable<{}> {
        return this.apiService.patch('users/' + id, request);
    }

    getUser(id: number): Observable<{
        userEditor: UserEditor
    }> {
        return this.apiService.get('users/' + id);
    }

    getPermissions(): Observable<{
        permissions: Permission[]
    }> {
        return this.apiService.get('permissions');
    }

    getCustomerGroup(id: number): Observable<{
        customerGroupEditor: CustomerGroupEditor
    }> {
        return this.apiService.get('customergroups/' + id);
    }

    createCustomerGroup(request: CustomerGroupEditor): Observable<{}> {
        return this.apiService.post('customergroups', request);
    }

    updateCustomerGroup(id: number, request: CustomerGroupEditor): Observable<{}> {
        return this.apiService.patch('customergroups/' + id, request);
    }

    deleteCustomerGroup(id: number): Observable<{}> {
        return this.apiService.delete('customergroups/' + id);
    }

    getCustomer(id: number): Observable<{
        customerEditor: CustomerEditor
    }> {
        return this.apiService.get('customers/' + id);
    }

    createCustomer(request: CustomerEditor): Observable<{}> {
        return this.apiService.post('customers', request);
    }

    updateCustomer(id: number, request: CustomerEditor): Observable<{}> {
        return this.apiService.patch('customers/' + id, request);
    }

    getCategories(): Observable<{
        categories: Category[]
    }> {
        return this.apiService.get('categories');
    }

    changeCategoryPositions(id: number, afterId: number | null, parentId: number | null): Observable<{}> {
        return this.apiService.patch('categories/' + id + '/changeposition' , {
            afterId,
            parentId
        });
    }

    getCategory(id: number): Observable<{
        categoryEditor: CategoryEditor
    }> {
        return this.apiService.get('categories/' + id);
    }

    createCategory(request: CategoryEditor, file: File|null = null): Observable<{}> {
        const data: FormData = new FormData();
        data.append('body', JSON.stringify(request));
        if (file !== null) {
            data.append('file', file);
        }
        return this.apiService.post('categories', data);
    }

    updateCategory(id: number, request: CategoryEditor, file: File|null = null): Observable<{}> {
        const data: FormData = new FormData();
        data.append('body', JSON.stringify(request));
        if (file !== null) {
            data.append('file', file);
        }
        return this.apiService.patch('categories/' + id, data);
    }

    deleteCategory(id: number): Observable<{
        precheckWarning?: string;
    }> {
        return this.apiService.delete('categories/' + id);
    }

    getProductAttribute(id: number): Observable<{
        attributeEditor: ProductAttributeEditor
    }> {
        return this.apiService.get('attributes/' + id);
    }

    createProductAttribute(request: ProductAttributeEditor): Observable<{}> {
        return this.apiService.post('attributes', request);
    }

    updateProductAttribute(id: number, request: ProductAttributeEditor): Observable<{}> {
        return this.apiService.patch('attributes/' + id, request);
    }

    getAttributesByCategories(ids: number[]): Observable<{
        attributes: ProductAttribute[]
    }> {
        return this.apiService.get('attributes/getbycategories?categories=' + ids.join(','));
    }

    deleteProductAttribute(id: number): Observable<{
        precheckWarning?: string;
    }> {
        return this.apiService.delete('attributes/' + id);
    }

    getProductPriceType(id: number): Observable<{
        productPriceTypeEditor: ProductPriceTypeEditor
    }> {
        return this.apiService.get('productpricetypes/' + id);
    }

    createProductPriceType(request: ProductPriceTypeEditor): Observable<{}> {
        return this.apiService.post('productpricetypes', request);
    }

    updateProductPriceType(id: number, request: ProductPriceTypeEditor): Observable<{}> {
        return this.apiService.patch('productpricetypes/' + id, request);
    }

    deletProductPriceType(id: number): Observable<{}> {
        return this.apiService.delete('productpricetypes/' + id);
    }

    createProduct(request: ProductEditor): Observable<{
        id: number
    }> {
        return this.apiService.post('products', request);
    }

    updateProduct(id: number, request: ProductEditor): Observable<{}> {
        return this.apiService.patch('products/' + id, request);
    }

    getProduct(id: number): Observable<{
        productEditor: ProductEditor
    }> {
        return this.apiService.get('products/' + id);
    }

    getSimpleTable<T>(endPoint: string, request: TableRequest|null = null): Observable<TableResponse<T>> {
        if (request === null) {
            return this.apiService.get(endPoint);
        } else {
            return this.apiService.get(endPoint + '?request=' + JSON.stringify(request));
        }
    }

    initProductsEditor(): Observable<{
        brands: string[];
        units: string[];
    }> {
        return this.apiService.get('products/initeditor');
    }

    tags(query: string): Observable<{
        tags: string[]
    }> {
        return this.apiService.get('tags?query=' + query);
    }

    getBanner(id: number): Observable<{
        bannerEditor: BannerEditor
    }> {
        return this.apiService.get('banners/' + id);
    }

    createBanner(bannerEditor: BannerEditor, file: File|null = null): Observable<{}> {
        const data: FormData = new FormData();
        data.append('body', JSON.stringify(bannerEditor));
        if (file !== null) {
            data.append('file', file);
        }
        return this.apiService.post('banners', data);
    }

    updateBanner(id: number, bannerEditor: BannerEditor, file: File|null = null): Observable<{}> {
        const data: FormData = new FormData();
        data.append('body', JSON.stringify(bannerEditor));
        if (file !== null) {
            data.append('file', file);
        }
        return this.apiService.patch('banners/' + id, data);
    }

    sortBanner(ids: number[]): Observable<{}> {
        return this.apiService.patch('banners/sort', {ids});
    }

    loadOrder(id: number): Observable<{
        orderEditor: OrderEditor
    }> {
        return this.apiService.get('orders/' + id);
    }

    updateOrder(id: number, data: any): Observable<{}> {
        return this.apiService.patch('orders/' + id, data);
    }

    loadFiles(): Observable<{
        files: DownloadableFile[]
    }> {
        return this.apiService.get('files');
    }

    uploadFile(fileName: string, file: File): Observable<{}> {
        const data: FormData = new FormData();
        data.append('file', file);
        return this.apiService.patch('files/' + fileName, data);
    }

    deleteFile(fileName: string): Observable<{}> {
        return this.apiService.delete('files/' + fileName);
    }

    getFaq(id: number): Observable<{
        faqEditor: FaqEditor
    }> {
        return this.apiService.get('faq/' + id);
    }

    createFaq(faqEditor: FaqEditor): Observable<{}> {
        return this.apiService.post('faq', faqEditor);
    }

    saveFaq(id: number, faqEditor: FaqEditor): Observable<{}> {
        return this.apiService.patch('faq/' + id, faqEditor);
    }

    sortFaq(ids: number[]): Observable<{}> {
        return this.apiService.patch('faq/sort', {ids});
    }

    deleteFaq(id: number): Observable<{}> {
        return this.apiService.delete('faq/' + id);
    }

    getPage(id: number): Observable<{
        pageEditor: PageEditor
    }> {
        return this.apiService.get('pages/' + id);
    }

    createPage(pageEditor: PageEditor): Observable<{}> {
        return this.apiService.post('pages', pageEditor);
    }

    savePage(id: number, pageEditor: PageEditor): Observable<{}> {
        return this.apiService.patch('pages/' + id, pageEditor);
    }

    deletePage(id: number): Observable<{}> {
        return this.apiService.delete('pages/' + id);
    }

    getModules(): Observable<{
        moduleGroups: AdminModuleGroup[]
    }> {
        return this.apiService.get('modules');
    }

    getModule(id: number): Observable<{
        module: AdminModuleDetail<any>
    }> {
        return this.apiService.get('modules/' + id);
    }

    enableModule(id: number, value: boolean): Observable<{}> {
        return this.apiService.patch('modules/' + id + '/enable', {
            value
        });
    }

    saveModuleConfig(id: number, config: any): Observable<{}> {
        return this.apiService.patch('modules/' + id, {
            config
        });
    }

    moduleFunctionGet<T>(id: number, functionName: string): Observable<{
        data: T
    }> {
        return this.apiService.get('modules/' + id + '/functions/' + functionName);
    }

    getModuleLog(id: number): Observable<{
        logs: AdminModuleLog[]
    }> {
        return this.apiService.get('modules/' + id + '/logs');
    }

    getModuleLogDetail(moduleId: number, id: number): Observable<{
        log: string;
    }> {
        return this.apiService.get('modules/' + moduleId + '/logs/' + id);
    }

    deleteCurrency(id: number): Observable<{}> {
        return this.apiService.delete('currencies/' + id);
    }

    getCurrency(id: number): Observable<{
        currencyEditor: CurrencyEditor
    }> {
        return this.apiService.get('currencies/' + id);
    }

    createCurrency(request: CurrencyEditor): Observable<{}> {
        return this.apiService.post('currencies', request);
    }

    updateCurrency(id: number, request: CurrencyEditor): Observable<{}> {
        return this.apiService.patch('currencies/' + id, request);
    }

    getSettings(): Observable<{
        settings: Settings
    }> {
        return this.apiService.get('settings');
    }

    saveSettings(settings: Settings): Observable<{}> {
        return this.apiService.patch('settings', settings);
    }

    getProductImageInfo(): Observable<{
        productImageInfo: ProductImageInfo[]
    }> {
        return this.apiService.get('products/images');
    }

    downloadProductImageInfo(slug: string): Observable<{
        content: any,
        filename: string
    }> {
        return this.apiService.get('products/images/' + slug);
    }

    getPaymentMethod(id: string): Observable<{
        paymentmethodEditor: PaymentmethodEditor
    }> {
        return this.apiService.get('paymentmethods/' + id);
    }

    updatePaymentMethod(id: string, paymentmethodEditor: PaymentmethodEditor): Observable<{}> {
        return this.apiService.patch('paymentmethods/' + id, paymentmethodEditor);
    }

    getShippingMethod(id: string): Observable<{
        shippingmethodEditor: ShippingMethodEditor
    }> {
        return this.apiService.get('shippingmethods/' + id);
    }

    updateShippingMethod(id: string, shippingmethodEditor: ShippingMethodEditor): Observable<{}> {
        return this.apiService.patch('shippingmethods/' + id, shippingmethodEditor);
    }

    sortProductsGratis(ids: number[]): Observable<{}> {
        return this.apiService.post('productsgratis/sort', {ids});
    }

    getProductsGratisList(): Observable<{
        gratis: GratisItem[]
    }> {
        return this.apiService.get('productsgratis');
    }

    createProductsGratis(gratisEditor: GratisEditor): Observable<{
        id: number
    }> {
        return this.apiService.post('productsgratis', gratisEditor);
    }

    updateProductsGratis(id: number, gratisEditor: GratisEditor): Observable<{}> {
        return this.apiService.patch('productsgratis/' + id, gratisEditor);
    }

    deleteProductsGratis(id: number): Observable<{}> {
        return this.apiService.delete('productsgratis/' + id);
    }

    getProductsGratis(id: number): Observable<{
        gratisEditor: GratisEditor
    }> {
        return this.apiService.get('productsgratis/' + id);
    }

    getBlog(id: number): Observable<{
        blogEditor: BlogEditor
    }> {
        return this.apiService.get('blogs/' + id);
    }

    createBlog(blogEditor: BlogEditor, file: File = null): Observable<{}> {
        const data: FormData = new FormData();
        data.append('body', JSON.stringify(blogEditor));
        if (file !== null) {
            data.append('file', file);
        }
        return this.apiService.post('blogs', data);
    }

    updateBlog(id: number, blogEditor: BlogEditor, file: File = null): Observable<{}> {
        const data: FormData = new FormData();
        data.append('body', JSON.stringify(blogEditor));
        if (file !== null) {
            data.append('file', file);
        }
        return this.apiService.patch('blogs/' + id, data);
    }

    deleteBlog(id: number): Observable<{}> {
        return this.apiService.delete('blogs/' + id);
    }
}
